// FMX visibility formulas — rules R4, R5 and R6 of the form-building rules.
//
// A visibility formula decides whether a field is shown (true) or hidden
// (false). Placeholders are field names in curly braces: {Do you need
// catering?}==true.
//
// ── THE ONE FACT THAT SHAPES THIS WHOLE MODULE ─────────────────────────────
// Visibility formulas CANNOT be set through the FMX API. The write model for
// POST/PUT /v1/custom-fields (Fmx.ApiCore/Models/CustomFields/
// CustomFieldEditModel.cs) has no VisibilityFormula property; only the READ
// model does (CustomFieldReadModel.cs:35, mapped in CustomFieldReadModelMapper
// .cs:50-51). So this module GENERATES and VALIDATES formula text that a human
// pastes into FMX by hand after the fields are created — hence the
// "Copy visibility formula" affordance in the builder and on the dry run.
//
// ── GROUND TRUTH (verified in fmx-app on 2026-07-30; re-verify here if the
//    formula engine changes) ─────────────────────────────────────────────────
// · Which fields can HAVE a formula: every type —
//   CustomFieldType.CanUseVisibilityFormula() returns true unconditionally
//   (Fmx.Model/CustomFields/CustomFieldType.cs:105).
// · Which fields can BE REFERENCED by one: text, url, number, currency,
//   checkbox, dropDownList, date — CanBeUsedInFormulas() (same file, :117).
//   NOT time, attachments, user, readOnly.
// · The data type a referenced field contributes (CustomFieldPlaceholder.cs
//   :35-54): text/url → string · number/currency → number · checkbox →
//   boolean · dropDownList single → string · dropDownList MULTI → array ·
//   date → date. Getting this wrong produces a formula FMX silently
//   evaluates as false, which is why buildCondition() is type-driven.
// · Placeholders bind BY NAME (CustomField.cs:226,
//   PlaceholderDisplayKey => Name), and duplicate names resolve by ordering
//   (comment at CustomField.cs:432-434) — so an ambiguous name is a hard
//   validation error here, not a warning.
// · Custom-field placeholders are drawn from the SAME FIELDSET only
//   (IPlaceholderHelpers.GetCustomFieldPlaceholders(org, fieldset), called
//   per-fieldset from e.g. ScheduleRequestDetailsPlaceholder.cs:42).
//
// Pure module: no React, no Supabase, no fetch — node-testable.

import { normalizeFieldType, fieldTypeLabel } from './fmxCustomFieldsApi.js';
import { normalizeFieldset, fieldsetLabel } from './fieldsets.js';

/**
 * Native (system) placeholders per fieldset, with the data type each
 * contributes. Transcribed from the IPlaceholder implementations in
 * Fmx.Model/PlaceholderSyntax/Placeholders/ — ONLY those whose
 * CanBeUsedInFormulas returns true are listed, because the others cannot
 * legally appear in a formula.
 *
 * Note the deliberate asymmetry, straight from the source: on a work request
 * {Building} is singular and a STRING (WorkRequestDetailsPlaceholder.cs:98,
 * :113), while on a schedule request {Buildings} is plural and an ARRAY
 * (ScheduleRequestDetailsPlaceholder.cs:134, :149). Start/end times, schedule,
 * requesting user, module, name, location, due and equipment are all
 * CanBeUsedInFormulas == false and so are absent on purpose.
 *
 * A fieldset that is absent here contributes no native placeholders; its
 * fields can still reference other custom fields in the same fieldset.
 */
export const NATIVE_PLACEHOLDERS_BY_FIELDSET = {
  workRequestDetails: [
    { name: 'Request type', dataType: 'string' },
    { name: 'Building', dataType: 'string' },
  ],
  scheduleRequestDetails: [
    { name: 'Request type', dataType: 'string' },
    { name: 'Buildings', dataType: 'array' },
    { name: 'Resources', dataType: 'array' },
    { name: 'Entrances', dataType: 'array' },
  ],
};

export function nativePlaceholdersFor(fieldset) {
  return NATIVE_PLACEHOLDERS_BY_FIELDSET[normalizeFieldset(fieldset)] || [];
}

// The data type a field contributes when referenced, or null when the type
// cannot be referenced at all.
export function formulaDataTypeOf(field) {
  switch (normalizeFieldType(field?.fieldType)) {
    case 'text':
    case 'url': return 'string';
    case 'number':
    case 'currency': return 'number';
    case 'checkbox': return 'boolean';
    case 'dropDownList': return field?.allowMultipleSelections ? 'array' : 'string';
    case 'date': return 'date';
    default: return null; // time, attachments, user, readOnly
  }
}

export function canBeReferenced(field) {
  return formulaDataTypeOf(field) !== null;
}

// FMX string literals are double-quoted. The escaping rules for an embedded
// double quote are not documented, so rather than guess we refuse to build
// the condition and say why — the user can rename the option or hand-write it.
function quote(value) {
  const s = String(value);
  if (s.includes('"')) {
    throw new Error(`The value “${s}” contains a double quote, which can’t be placed in an FMX formula literal. Rename the option, or write this formula by hand.`);
  }
  return `"${s}"`;
}

export const OPERATORS = {
  equals: '==', notEquals: '!=',
  greaterThan: '>', greaterOrEqual: '>=',
  lessThan: '<', lessOrEqual: '<=',
  includes: 'includes', notIncludes: '!includes',
};

/**
 * Build ONE condition, with syntax driven by the referenced field's data
 * type (R6). Returns a string; throws only for unquotable values.
 *
 * @param name      the referenced field's exact name (placeholder text)
 * @param dataType  'boolean' | 'string' | 'array' | 'number' | 'date'
 * @param operator  key of OPERATORS ('equals' by default)
 * @param value     the compared value; for booleans, true/false or a
 *                  yes/no-ish string
 */
export function buildCondition({ name, dataType, operator = 'equals', value }) {
  const ref = `{${name}}`;

  if (dataType === 'boolean') {
    // A checkbox is boolean: the documented FMX form is {Field}==true.
    // "No answer triggers …" (which the sample document really does contain)
    // must therefore produce ==false, not a negated ==true.
    const truthy = value === true
      || /^(true|yes|y|checked|1)$/i.test(String(value ?? 'true'));
    const wantTrue = operator === 'notEquals' ? !truthy : truthy;
    return `${ref}==${wantTrue ? 'true' : 'false'}`;
  }

  if (dataType === 'array') {
    // Multi-select dropdowns and the native list placeholders are arrays —
    // membership, never equality.
    if (operator === 'notIncludes' || operator === 'notEquals') return `!includes(${ref}, ${quote(value)})`;
    return `includes(${ref}, ${quote(value)})`;
  }

  if (dataType === 'number') {
    const op = OPERATORS[operator] && !['includes', '!includes'].includes(OPERATORS[operator])
      ? OPERATORS[operator] : '==';
    const num = Number(value);
    return `${ref} ${op} ${Number.isFinite(num) ? num : quote(value)}`;
  }

  // string and date compare by equality/inequality against a literal.
  const op = operator === 'notEquals' ? '!=' : '==';
  return `${ref}${op}${quote(value)}`;
}

/** Join conditions with && / ||, parenthesizing when there's more than one. */
export function composeFormula(conditions, join = 'and') {
  const parts = (conditions || []).filter(c => c && String(c).trim());
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  const glue = join === 'or' ? ' || ' : ' && ';
  return parts.map(c => `(${c})`).join(glue);
}

const PLACEHOLDER_RE = /\{([^{}]*)\}/g;

export function placeholdersIn(formula) {
  const out = [];
  let m;
  const re = new RegExp(PLACEHOLDER_RE.source, 'g');
  while ((m = re.exec(String(formula || ''))) !== null) out.push(m[1].trim());
  return out;
}

/**
 * Validate a formula against the draft it belongs to (R5). Deterministic —
 * this is the gate that keeps a copy-pasted formula from silently evaluating
 * to false in FMX.
 *
 * @param formula the formula text
 * @param ctx
 *   fieldset     — the fieldset the owning field lives in
 *   draftFields  — all draft fields (any fieldset; cross-fieldset refs are an
 *                  error, and we need them to say so precisely)
 *   selfName     — the owning field's name, to catch self-reference
 * @returns {{ errors: string[], warnings: string[], references: object[] }}
 */
export function validateFormula(formula, ctx = {}) {
  const errors = [];
  const warnings = [];
  const references = [];
  const { fieldset, draftFields = [], selfName = null } = ctx;
  const text = String(formula || '').trim();

  if (!text) return { errors, warnings, references };

  // Unbalanced braces would break placeholder parsing entirely.
  const opens = (text.match(/\{/g) || []).length;
  const closes = (text.match(/\}/g) || []).length;
  if (opens !== closes) {
    errors.push('The formula has unbalanced { } braces around a field name.');
    return { errors, warnings, references };
  }
  if (!opens) {
    warnings.push('This formula references no fields, so it always evaluates the same way.');
  }

  const fs = normalizeFieldset(fieldset);
  const natives = nativePlaceholdersFor(fs);

  for (const name of placeholdersIn(text)) {
    if (!name) { errors.push('The formula contains an empty {} placeholder.'); continue; }

    const native = natives.find(n => n.name.toLowerCase() === name.toLowerCase());
    if (native) { references.push({ name, kind: 'native', dataType: native.dataType }); continue; }

    // Placeholders bind by exact name, so compare exact (trimmed) names.
    const sameFieldset = draftFields.filter(f =>
      normalizeFieldset(f.fieldset) === fs && String(f.name || '').trim() === name);

    if (sameFieldset.length === 0) {
      const elsewhere = draftFields.find(f => String(f.name || '').trim() === name);
      if (elsewhere) {
        errors.push(`“${name}” is in ${fieldsetLabel(elsewhere.fieldset)}, but a formula can only reference fields in the same fieldset (${fieldsetLabel(fs)}).`);
      } else {
        const nativeNames = natives.map(n => `{${n.name}}`).join(', ');
        errors.push(`There is no field named “${name}” in ${fieldsetLabel(fs)}.${nativeNames ? ` Built-in placeholders available here: ${nativeNames}.` : ''}`);
      }
      continue;
    }
    if (sameFieldset.length > 1) {
      errors.push(`“${name}” matches ${sameFieldset.length} fields in ${fieldsetLabel(fs)} — FMX resolves a duplicated name unpredictably. Merge them into one field first.`);
      continue;
    }

    const [ref] = sameFieldset;
    if (selfName && String(ref.name).trim() === String(selfName).trim()) {
      errors.push('A field’s visibility formula can’t reference the field itself.');
      continue;
    }
    const dataType = formulaDataTypeOf(ref);
    if (!dataType) {
      errors.push(`“${name}” is a ${fieldTypeLabel(ref.fieldType)} field, which FMX can’t use in a formula. Reference a text, number, currency, checkbox, drop down, date, or URL field instead.`);
      continue;
    }
    references.push({ name, kind: 'custom', dataType, fieldId: ref.id });
  }

  return { errors, warnings, references };
}

/**
 * Compose the formula for one stage-2 visibility rule.
 *
 * A rule is what the AI emits — WHICH question triggers this one and on what
 * value — never the formula string itself; code builds the syntax so it always
 * matches the trigger's live type (the house convention: the AI proposes
 * attributes, code assembles payloads).
 *
 * @param rule { dependsOnSourceItemId, operator, value, rawText }
 * @param bySourceItem Map from stage-1 item id → draft field
 *        (mergeFields.fieldBySourceItem)
 * @returns {{ formula, trigger, dataType, error }} — error is a plain-language
 *          reason the rule can't become a formula (trigger missing, or of a
 *          type FMX can't reference), in which case formula is null.
 */
export function formulaForRule(rule, bySourceItem) {
  const trigger = bySourceItem?.get?.(rule?.dependsOnSourceItemId) || null;
  if (!trigger) {
    return { formula: null, trigger: null, dataType: null,
      error: 'The question this one depends on isn’t a custom field on this form (it may be native, dismissed, or unread), so the condition can’t be built automatically.' };
  }
  const dataType = formulaDataTypeOf(trigger);
  if (!dataType) {
    return { formula: null, trigger, dataType: null,
      error: `“${trigger.name}” is a ${fieldTypeLabel(trigger.fieldType)} field, which FMX can’t reference in a formula. Change its type, or set this field’s visibility by hand.` };
  }
  try {
    const formula = buildCondition({
      name: String(trigger.name || '').trim(),
      dataType,
      operator: rule?.operator || 'equals',
      value: rule?.value,
    });
    return { formula, trigger, dataType, error: null };
  } catch (err) {
    return { formula: null, trigger, dataType, error: err.message };
  }
}

/**
 * Derive formulas for a whole draft from the stage-2 visibility rules.
 * Returns a new fields array with `visibilityFormula` (string) and
 * `visibilityRule` (the rule plus its resolution) set where derivable, and a
 * list of rules that could not be turned into formulas so the UI can show
 * them as manual follow-ups rather than dropping them.
 *
 * Never overwrites a formula the user has edited by hand
 * (`visibilityFormulaEdited: true`).
 */
export function applyVisibilityRules(fields, visibilityRules, bySourceItem) {
  const unresolved = [];
  const ruleFor = new Map();
  for (const rule of visibilityRules || []) {
    if (rule?.sourceItemId) ruleFor.set(rule.sourceItemId, rule);
  }

  const next = (fields || []).map(f => {
    const ids = f.mergedFrom?.length ? f.mergedFrom : [f.sourceItemId];
    const rule = ids.map(id => ruleFor.get(id)).find(Boolean);
    if (!rule) return { ...f };
    const resolved = formulaForRule(rule, bySourceItem);
    if (resolved.error) {
      unresolved.push({ ...rule, fieldName: f.name, reason: resolved.error });
      return { ...f, visibilityRule: { ...rule, error: resolved.error } };
    }
    if (f.visibilityFormulaEdited) return { ...f };
    return {
      ...f,
      visibilityFormula: resolved.formula,
      visibilityRule: { ...rule, triggerFieldId: resolved.trigger.id, triggerName: resolved.trigger.name },
    };
  });

  return { fields: next, unresolved };
}

// One human-readable line per formula, for the "apply these after pushing"
// checklist on the dry run (and, in Milestone 3, the results screen).
export function formulaChecklist(fields) {
  return (fields || [])
    .filter(f => f.visibilityFormula)
    .map(f => ({
      fieldName: f.name,
      fieldset: normalizeFieldset(f.fieldset),
      formula: f.visibilityFormula,
      because: f.visibilityRule?.rawText || null,
    }));
}
